// "Recent runs" in Agents' overview (agents.runs, gap #18): the latest runs of every agent, or one,
// newest first: what each was asked, how it ended and its steps, a flag in words, and the cost for
// the owner only (agents.manage). Open run shows its steps (agents.getRun).
// BL-189: RowList rows, under the overview's Recent runs Section; the run opens under its row.

import { useState } from 'react';
import { RowList, SelectField, type Row } from '@flowology/ui-web/fl';
import { useQuery } from '../books/ui.tsx';
import { taskText } from './model.ts';
import { RunPanel } from './RunPanel.tsx';
import { PartSays, agentOptions, toneOf } from './agents-parts.tsx';
import type { AgentsProps } from './props.ts';
import {
  costText,
  flagWords,
  runLine,
  runStatus,
  startedText,
  triggerWords,
  type RunSummary,
} from './runs-model.ts';

/** One run, a row: the agent and what started it, what it was asked, how it ended; Open run shows it. */
function runRow(
  r: RunSummary,
  o: {
    names: Map<string, string>;
    props: AgentsProps;
    open: string | null;
    onOpen: (id: string | null) => void;
  },
): Row {
  const [label, tone] = runStatus(r.status);
  const name = o.names.get(r.agentKey) ?? r.agentKey;
  const started = startedText(
    r.startedAt,
    o.props.timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
  );
  const shown = o.open === r.id;
  return {
    id: r.id,
    name,
    kicker: started,
    after: { text: ` · ${triggerWords(r.trigger)}`, size: 'sm' },
    lines: [
      { text: taskText(r.task), tone: 'ink', size: 'sm' },
      {
        text: `${runLine(r)}${o.props.isOwner && r.costCents !== undefined ? ` · model cost ${costText(r.costCents)}` : ''}`,
      },
      ...flagWords(r.flags).map((f) => ({ word: f, tone: 'bad' as const })),
      ...(shown
        ? [{ part: <RunPanel runId={r.id} timeZone={o.props.timeZone} showCost={o.props.isOwner} /> }]
        : []),
    ],
    end: { word: label, tone: toneOf(tone) },
    actions: [
      {
        id: 'run',
        text: shown ? 'Hide run' : 'Open run',
        label: `${shown ? 'Hide' : 'Open'} run: ${name}, ${started}`,
        onPress: () => o.onOpen(shown ? null : r.id),
      },
    ],
  };
}

export function RecentRuns({ props, names }: { props: AgentsProps; names: Map<string, string> }) {
  const [agentKey, setAgentKey] = useState('');
  const [open, setOpen] = useState<string | null>(null);
  const runs = useQuery<{ runs: RunSummary[] }>(
    'agents.runs',
    agentKey ? { agentKey, limit: 25 } : { limit: 25 },
  );
  return (
    <>
      <div className="agents-tools">
        <SelectField
          label="Agent"
          hideLabel
          width="fit"
          value={agentKey}
          onChange={setAgentKey}
          options={agentOptions(names)}
        />
      </div>
      <PartSays
        status={runs.data ? 'ready' : runs.error ? 'failed' : 'loading'}
        what="the recent runs"
        failedText={runs.error ?? undefined}
        onRetry={() => void runs.reload()}
      >
        <RowList
          label="Recent runs"
          rows={(runs.data?.runs ?? []).map((r) => runRow(r, { names, props, open, onOpen: setOpen }))}
          empty="No runs yet."
        />
      </PartSays>
    </>
  );
}
